// This file is auto-generated by @hey-api/openapi-ts

import type { Client, Options as Options2, TDataShape } from "./client";
import { client } from "./client.gen";
import type {
  AdminActivityActionsData,
  AdminActivityActionsResponses,
  AdminActivityAnalyticsData,
  AdminActivityAnalyticsErrors,
  AdminActivityAnalyticsResponses,
  AdminCreateAccountData,
  AdminCreateAccountErrors,
  AdminCreateAccountResponses,
  AdminCreateContactData,
  AdminCreateContactErrors,
  AdminCreateContactResponses,
  AdminDeleteUserData,
  AdminDeleteUserErrors,
  AdminDeleteUserResponses,
  AdminGetAccountPermissionsData,
  AdminGetAccountPermissionsErrors,
  AdminGetAccountPermissionsResponses,
  AdminGetMcpConnectionInfoData,
  AdminGetMcpConnectionInfoResponses,
  AdminGetUserData,
  AdminGetUserErrors,
  AdminGetUserResponses,
  AdminInviteUserData,
  AdminInviteUserErrors,
  AdminInviteUserResponses,
  AdminListAccountsData,
  AdminListAccountsResponses,
  AdminListActivityData,
  AdminListActivityErrors,
  AdminListActivityResponses,
  AdminListMcpAuditData,
  AdminListMcpAuditErrors,
  AdminListMcpAuditResponses,
  AdminListUsersData,
  AdminListUsersResponses,
  AdminMcpAnalyticsData,
  AdminMcpAnalyticsErrors,
  AdminMcpAnalyticsResponses,
  AdminMcpToolNamesData,
  AdminMcpToolNamesResponses,
  AdminSetAccountMcpData,
  AdminSetAccountMcpErrors,
  AdminSetAccountMcpResponses,
  AdminSetAccountPermissionData,
  AdminSetAccountPermissionErrors,
  AdminSetAccountPermissionResponses,
  AdminUpdateAccountData,
  AdminUpdateAccountErrors,
  AdminUpdateAccountResponses,
  AdminUpdateContactData,
  AdminUpdateContactErrors,
  AdminUpdateContactResponses,
  AdminUpdateUserData,
  AdminUpdateUserErrors,
  AdminUpdateUserResponses,
  CustomerApproveOrderData,
  CustomerApproveOrderErrors,
  CustomerApproveOrderResponses,
  CustomerGetPermissionsData,
  CustomerGetPermissionsResponses,
  CustomerInviteColleagueData,
  CustomerInviteColleagueErrors,
  CustomerInviteColleagueResponses,
  CustomerListPendingOrdersData,
  CustomerListPendingOrdersResponses,
  CustomerRejectOrderData,
  CustomerRejectOrderErrors,
  CustomerRejectOrderResponses,
  CustomerSetColleagueActiveData,
  CustomerSetColleagueActiveErrors,
  CustomerSetColleagueActiveResponses,
  CustomerSetPermissionData,
  CustomerSetPermissionErrors,
  CustomerSetPermissionResponses,
  CustomerSsoCallbackData,
  CustomerSsoLoginData,
  CustomerSsoLoginErrors,
  GetActivityData,
  GetActivityResponses,
  GetHealthData,
  GetHealthResponses,
  GetMeData,
  GetMeDetailData,
  GetMeDetailResponses,
  GetMeResponses,
  LogoutData,
  LogoutResponses,
  SsoCallbackData,
  SsoLoginData,
  SsoLoginErrors,
  UpdateProfileData,
  UpdateProfileErrors,
  UpdateProfileResponses,
} from "./types.gen";

export type Options<
  TData extends TDataShape = TDataShape,
  ThrowOnError extends boolean = boolean,
  TResponse = unknown,
> = Options2<TData, ThrowOnError, TResponse> & {
  /**
   * You can provide a client instance returned by `createClient()` instead of
   * individual options. This might be also useful if you want to implement a
   * custom client.
   */
  client?: Client;
  /**
   * You can pass arbitrary values through the `meta` object. This can be
   * used to access values that aren't defined as part of the SDK function.
   */
  meta?: Record<string, unknown>;
};

/**
 * Customer Get Permissions
 *
 * Return the caller's own account permission picture (customer account-admin only).
 *
 * The same payload shape the admin dashboard renders, scoped to the caller's
 * account. 403 unless the caller resolves to allow on `(account, admin)`.
 */
export const customerGetPermissions = <ThrowOnError extends boolean = false>(
  options?: Options<CustomerGetPermissionsData, ThrowOnError>,
) =>
  (options?.client ?? client).get<CustomerGetPermissionsResponses, unknown, ThrowOnError>({
    url: "/customer/permissions",
    ...options,
  });

/**
 * Customer Set Permission
 *
 * Set or clear one colleague's permission grant on the caller's account.
 *
 * A customer may only edit a specific colleague's override (`contact_id` is
 * required) for a `customer_grantable` capability, never the account default. The
 * write may overwrite or clear ANY per-contact grant regardless of who authored
 * it (a J&J-authored row included) and always stamps the row `customer`. The
 * written effect takes effect as-is — per-contact rows win outright, with no
 * ceiling capping (JJFW-840). The one thing a
 * customer admin may not do is remove their OWN `(account, admin)` right — that
 * self un-admin is a 400, mirroring the self-deactivation block on colleagues.
 * Returns the refreshed payload.
 */
export const customerSetPermission = <ThrowOnError extends boolean = false>(
  options: Options<CustomerSetPermissionData, ThrowOnError>,
) =>
  (options.client ?? client).put<
    CustomerSetPermissionResponses,
    CustomerSetPermissionErrors,
    ThrowOnError
  >({
    url: "/customer/permissions",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Customer Invite Colleague
 *
 * Add a colleague to the caller's own account (customer account-admin only).
 *
 * The email is the colleague's whole sign-in identity (federated to the customer
 * IdP), normalized `strip().lower()` and unique across all accounts (409 on a
 * collision). Returns the refreshed account permission payload.
 */
export const customerInviteColleague = <ThrowOnError extends boolean = false>(
  options: Options<CustomerInviteColleagueData, ThrowOnError>,
) =>
  (options.client ?? client).post<
    CustomerInviteColleagueResponses,
    CustomerInviteColleagueErrors,
    ThrowOnError
  >({
    url: "/customer/colleagues",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Customer Set Colleague Active
 *
 * Activate or deactivate a colleague on the caller's account (account-admin only).
 *
 * The target must belong to the caller's account (404 otherwise), and a customer
 * cannot deactivate their own account (400). Returns the refreshed payload.
 */
export const customerSetColleagueActive = <ThrowOnError extends boolean = false>(
  options: Options<CustomerSetColleagueActiveData, ThrowOnError>,
) =>
  (options.client ?? client).put<
    CustomerSetColleagueActiveResponses,
    CustomerSetColleagueActiveErrors,
    ThrowOnError
  >({
    url: "/customer/colleagues/{contact_id}",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Customer List Pending Orders
 *
 * List the caller's account orders awaiting approval (approvers only).
 *
 * 403 unless the caller resolves to allow on `(orders, approve)`. Orders are
 * oldest-first, each with its placed-by contact and line summary.
 */
export const customerListPendingOrders = <ThrowOnError extends boolean = false>(
  options?: Options<CustomerListPendingOrdersData, ThrowOnError>,
) =>
  (options?.client ?? client).get<CustomerListPendingOrdersResponses, unknown, ThrowOnError>({
    url: "/customer/orders/pending",
    ...options,
  });

/**
 * Customer Approve Order
 *
 * Approve a pending order on the caller's account, submitting it to J&J.
 */
export const customerApproveOrder = <ThrowOnError extends boolean = false>(
  options: Options<CustomerApproveOrderData, ThrowOnError>,
) =>
  (options.client ?? client).post<
    CustomerApproveOrderResponses,
    CustomerApproveOrderErrors,
    ThrowOnError
  >({ url: "/customer/orders/{order_id}/approve", ...options });

/**
 * Customer Reject Order
 *
 * Reject a pending order on the caller's account, recording an optional reason.
 */
export const customerRejectOrder = <ThrowOnError extends boolean = false>(
  options: Options<CustomerRejectOrderData, ThrowOnError>,
) =>
  (options.client ?? client).post<
    CustomerRejectOrderResponses,
    CustomerRejectOrderErrors,
    ThrowOnError
  >({
    url: "/customer/orders/{order_id}/reject",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Health
 *
 * Return a simple health status payload.
 */
export const getHealth = <ThrowOnError extends boolean = false>(
  options?: Options<GetHealthData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetHealthResponses, unknown, ThrowOnError>({
    url: "/health",
    ...options,
  });

/**
 * Auth Sso Login
 *
 * Begin Entra ID SSO — redirect the browser to Microsoft to sign in.
 *
 * Stashes a safe post-login destination in the short-lived OIDC session, then
 * hands off to Entra's authorize endpoint (Authorization Code + PKCE). The
 * browser returns to `/auth/sso/callback` with an authorization code.
 */
export const ssoLogin = <ThrowOnError extends boolean = false>(
  options?: Options<SsoLoginData, ThrowOnError>,
) =>
  (options?.client ?? client).get<unknown, SsoLoginErrors, ThrowOnError>({
    url: "/auth/sso/login",
    ...options,
  });

/**
 * Auth Sso Callback
 *
 * Complete Entra ID SSO and start an app session.
 *
 * Exchanges the authorization code for an ID token, enforces the email-domain
 * allowlist, JIT-provisions the user, persists the encrypted refresh token,
 * mints the HMAC session cookie, and redirects back into the SPA. Any failure
 * redirects to `{FRONTEND_URL}/login?error=<code>`.
 */
export const ssoCallback = <ThrowOnError extends boolean = false>(
  options?: Options<SsoCallbackData, ThrowOnError>,
) =>
  (options?.client ?? client).get<unknown, unknown, ThrowOnError>({
    url: "/auth/sso/callback",
    ...options,
  });

/**
 * Auth Customer Login
 *
 * Begin customer portal sign-in — redirect to the customer CIAM tenant.
 *
 * Mirrors `/auth/sso/login` but uses the customer OIDC client (the CIAM tenant),
 * not the J&J workforce tenant. 503 when the customer IdP is unconfigured
 * (fail-closed). The browser returns to `/auth/customer/callback`.
 */
export const customerSsoLogin = <ThrowOnError extends boolean = false>(
  options?: Options<CustomerSsoLoginData, ThrowOnError>,
) =>
  (options?.client ?? client).get<unknown, CustomerSsoLoginErrors, ThrowOnError>({
    url: "/auth/customer/login",
    ...options,
  });

/**
 * Auth Customer Callback
 *
 * Complete customer CIAM sign-in and start a portal session.
 *
 * Exchanges the code for an ID token, resolves the verified email to an ACTIVE
 * contact whose account has MCP enabled, mints the HMAC session cookie with role
 * `customer`, and redirects into the SPA. A verified email with no matching
 * customer account redirects to `?error=no-customer-account`; any exchange/claim
 * failure redirects with the matching error code. No refresh token is stored —
 * the CIAM client mints none, so the session simply expires.
 */
export const customerSsoCallback = <ThrowOnError extends boolean = false>(
  options?: Options<CustomerSsoCallbackData, ThrowOnError>,
) =>
  (options?.client ?? client).get<unknown, unknown, ThrowOnError>({
    url: "/auth/customer/callback",
    ...options,
  });

/**
 * Auth Logout
 *
 * Sign out — revoke the stored refresh token and clear the session cookie.
 */
export const logout = <ThrowOnError extends boolean = false>(
  options?: Options<LogoutData, ThrowOnError>,
) =>
  (options?.client ?? client).post<LogoutResponses, unknown, ThrowOnError>({
    url: "/auth/logout",
    ...options,
  });

/**
 * Auth Me
 *
 * Get the current identity's profile — a workforce user OR a portal customer.
 *
 * `kind` distinguishes the two. A customer (JJFW-840) resolves from their
 * `contacts` row rather than the `users` table, and carries account context plus
 * the two resolved portal capabilities; a workforce user is unchanged.
 */
export const getMe = <ThrowOnError extends boolean = false>(
  options?: Options<GetMeData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetMeResponses, unknown, ThrowOnError>({
    url: "/auth/me",
    ...options,
  });

/**
 * Auth Me Detail
 *
 * Get the current user's profile with timeline fields.
 */
export const getMeDetail = <ThrowOnError extends boolean = false>(
  options?: Options<GetMeDetailData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetMeDetailResponses, unknown, ThrowOnError>({
    url: "/auth/me/detail",
    ...options,
  });

/**
 * Auth Update Profile
 *
 * Update current user's name.
 */
export const updateProfile = <ThrowOnError extends boolean = false>(
  options: Options<UpdateProfileData, ThrowOnError>,
) =>
  (options.client ?? client).put<UpdateProfileResponses, UpdateProfileErrors, ThrowOnError>({
    url: "/auth/profile",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Auth Activity
 *
 * Get the current user's activity log.
 */
export const getActivity = <ThrowOnError extends boolean = false>(
  options?: Options<GetActivityData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetActivityResponses, unknown, ThrowOnError>({
    url: "/auth/activity",
    ...options,
  });

/**
 * Admin List Users
 *
 * List all users (admin only).
 */
export const adminListUsers = <ThrowOnError extends boolean = false>(
  options?: Options<AdminListUsersData, ThrowOnError>,
) =>
  (options?.client ?? client).get<AdminListUsersResponses, unknown, ThrowOnError>({
    url: "/admin/users",
    ...options,
  });

/**
 * Admin Delete User
 *
 * Delete a user (admin only). Self-deletion blocked.
 */
export const adminDeleteUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminDeleteUserData, ThrowOnError>,
) =>
  (options.client ?? client).delete<AdminDeleteUserResponses, AdminDeleteUserErrors, ThrowOnError>({
    url: "/admin/users/{user_id}",
    ...options,
  });

/**
 * Admin Get User
 *
 * Get a single user detail with activity logs (admin only).
 */
export const adminGetUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminGetUserData, ThrowOnError>,
) =>
  (options.client ?? client).get<AdminGetUserResponses, AdminGetUserErrors, ThrowOnError>({
    url: "/admin/users/{user_id}",
    ...options,
  });

/**
 * Admin Update User
 *
 * Update a user's name or role (admin only). Self-demotion blocked.
 */
export const adminUpdateUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminUpdateUserData, ThrowOnError>,
) =>
  (options.client ?? client).put<AdminUpdateUserResponses, AdminUpdateUserErrors, ThrowOnError>({
    url: "/admin/users/{user_id}",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Admin Invite User
 *
 * Pre-provision a user by email (admin only).
 *
 * Creates the user row ahead of time (no password, email verified) so the
 * person can sign straight in via Entra ID SSO. No credential is issued — the
 * identity provider owns authentication. 409 if the email already exists.
 */
export const adminInviteUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminInviteUserData, ThrowOnError>,
) =>
  (options.client ?? client).post<AdminInviteUserResponses, AdminInviteUserErrors, ThrowOnError>({
    url: "/admin/invite",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Admin List Activity
 *
 * List activity across all users (admin only).
 *
 * Search, filtering, sorting, and pagination are all applied server-side by
 * the data layer — the client sends params and renders results.
 */
export const adminListActivity = <ThrowOnError extends boolean = false>(
  options?: Options<AdminListActivityData, ThrowOnError>,
) =>
  (options?.client ?? client).get<
    AdminListActivityResponses,
    AdminListActivityErrors,
    ThrowOnError
  >({ url: "/admin/activity", ...options });

/**
 * Admin Activity Analytics
 *
 * Aggregated activity analytics for the admin dashboard (admin only).
 */
export const adminActivityAnalytics = <ThrowOnError extends boolean = false>(
  options?: Options<AdminActivityAnalyticsData, ThrowOnError>,
) =>
  (options?.client ?? client).get<
    AdminActivityAnalyticsResponses,
    AdminActivityAnalyticsErrors,
    ThrowOnError
  >({ url: "/admin/activity/analytics", ...options });

/**
 * Admin Activity Actions
 *
 * Distinct activity actions across all time (admin only).
 *
 * Used to populate the activity filter dropdown so it matches the full,
 * unbounded table scope rather than the windowed analytics breakdown.
 */
export const adminActivityActions = <ThrowOnError extends boolean = false>(
  options?: Options<AdminActivityActionsData, ThrowOnError>,
) =>
  (options?.client ?? client).get<AdminActivityActionsResponses, unknown, ThrowOnError>({
    url: "/admin/activity/actions",
    ...options,
  });

/**
 * Admin List Accounts
 *
 * List all customer accounts with their MCP enablement flag (admin only).
 */
export const adminListAccounts = <ThrowOnError extends boolean = false>(
  options?: Options<AdminListAccountsData, ThrowOnError>,
) =>
  (options?.client ?? client).get<AdminListAccountsResponses, unknown, ThrowOnError>({
    url: "/admin/accounts",
    ...options,
  });

/**
 * Admin Create Account
 *
 * Create a customer account with a minted `ACC-<n>` id (admin only).
 *
 * The id is the next number after the highest existing `ACC-*`; a primary-key
 * collision (a concurrent create) is retried once with a recomputed id, then
 * surfaced as 409. 400 when the account name is blank.
 */
export const adminCreateAccount = <ThrowOnError extends boolean = false>(
  options: Options<AdminCreateAccountData, ThrowOnError>,
) =>
  (options.client ?? client).post<
    AdminCreateAccountResponses,
    AdminCreateAccountErrors,
    ThrowOnError
  >({
    url: "/admin/accounts",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Admin Set Account Mcp
 *
 * Enable or disable an account's MCP access (admin only). 404 if unknown.
 */
export const adminSetAccountMcp = <ThrowOnError extends boolean = false>(
  options: Options<AdminSetAccountMcpData, ThrowOnError>,
) =>
  (options.client ?? client).put<
    AdminSetAccountMcpResponses,
    AdminSetAccountMcpErrors,
    ThrowOnError
  >({
    url: "/admin/accounts/{account_id}/mcp",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Admin Get Account Permissions
 *
 * Return an account's full MCP permission picture (admin only). 404 if unknown.
 *
 * Includes the enforceable capabilities, the account-level defaults, and every
 * contact's resolved per-capability state, so the dashboard grid renders
 * entirely from this one payload.
 */
export const adminGetAccountPermissions = <ThrowOnError extends boolean = false>(
  options: Options<AdminGetAccountPermissionsData, ThrowOnError>,
) =>
  (options.client ?? client).get<
    AdminGetAccountPermissionsResponses,
    AdminGetAccountPermissionsErrors,
    ThrowOnError
  >({ url: "/admin/accounts/{account_id}/permissions", ...options });

/**
 * Admin Set Account Permission
 *
 * Set or clear one MCP permission grant, returning the refreshed payload (admin only).
 *
 * A null `contact_id` edits the account-level default; a non-null one edits
 * that contact's override. `effect=null` clears the explicit grant so the
 * subject reverts to inheriting. 400 for an unknown capability, 404 for an
 * unknown account or a contact that isn't on this account.
 */
export const adminSetAccountPermission = <ThrowOnError extends boolean = false>(
  options: Options<AdminSetAccountPermissionData, ThrowOnError>,
) =>
  (options.client ?? client).put<
    AdminSetAccountPermissionResponses,
    AdminSetAccountPermissionErrors,
    ThrowOnError
  >({
    url: "/admin/accounts/{account_id}/permissions",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Admin Update Account
 *
 * Update an account's descriptive fields (admin only). 404 if unknown.
 *
 * Only fields present in the request body change: `segment`/`region` clear when
 * explicitly null, `account_name` cannot be cleared (400 if set to blank/null),
 * and omitted fields are left untouched.
 */
export const adminUpdateAccount = <ThrowOnError extends boolean = false>(
  options: Options<AdminUpdateAccountData, ThrowOnError>,
) =>
  (options.client ?? client).put<
    AdminUpdateAccountResponses,
    AdminUpdateAccountErrors,
    ThrowOnError
  >({
    url: "/admin/accounts/{account_id}",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Admin Create Contact
 *
 * Add a contact (customer_user) to an account (admin only).
 *
 * Emails are normalized (`strip().lower()`) and must be unique across ALL
 * accounts (case-insensitive), so a collision is a 409. The email is the
 * contact's whole MCP sign-in identity (login is federated to the customer IdP,
 * JJFW-765) — there is no credential to set. Returns the full refreshed account
 * permission payload so the detail page can replace its cache wholesale.
 */
export const adminCreateContact = <ThrowOnError extends boolean = false>(
  options: Options<AdminCreateContactData, ThrowOnError>,
) =>
  (options.client ?? client).post<
    AdminCreateContactResponses,
    AdminCreateContactErrors,
    ThrowOnError
  >({
    url: "/admin/accounts/{account_id}/contacts",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Admin Update Contact
 *
 * Update a contact (admin only), returning the refreshed account payload.
 *
 * Only fields present in the request body change: `email`/`role` clear when
 * explicitly null, `full_name` cannot be cleared (400), and `active=false`
 * revokes MCP access — the resource-server guard re-checks the contact live on
 * every token validation, so deactivation takes effect on the next call with no
 * token-invalidation step. Emails must stay unique across all accounts (409).
 * 404 if the contact is unknown or belongs to another account.
 */
export const adminUpdateContact = <ThrowOnError extends boolean = false>(
  options: Options<AdminUpdateContactData, ThrowOnError>,
) =>
  (options.client ?? client).put<
    AdminUpdateContactResponses,
    AdminUpdateContactErrors,
    ThrowOnError
  >({
    url: "/admin/accounts/{account_id}/contacts/{contact_id}",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Admin Get Mcp Connection Info
 *
 * Return the MCP connection kit for a customer to wire up their client (admin only).
 *
 * Carries no secrets — only public URLs derived from the MCP OAuth settings.
 * `configured` is false when the signing key is unset, meaning the OAuth
 * surface is not yet live.
 */
export const adminGetMcpConnectionInfo = <ThrowOnError extends boolean = false>(
  options?: Options<AdminGetMcpConnectionInfoData, ThrowOnError>,
) =>
  (options?.client ?? client).get<AdminGetMcpConnectionInfoResponses, unknown, ThrowOnError>({
    url: "/admin/mcp-connection",
    ...options,
  });

/**
 * Admin Mcp Analytics
 *
 * Aggregated MCP usage insights for the admin dashboard (admin only).
 *
 * `days` is clamped to 1..365; an optional `account_id` scopes the summary,
 * timeseries, tool, and denied views to one customer (the account and order
 * funnel stay platform-wide). The reads run sequentially — a single
 * AsyncSession is not safe for concurrent use.
 */
export const adminMcpAnalytics = <ThrowOnError extends boolean = false>(
  options?: Options<AdminMcpAnalyticsData, ThrowOnError>,
) =>
  (options?.client ?? client).get<
    AdminMcpAnalyticsResponses,
    AdminMcpAnalyticsErrors,
    ThrowOnError
  >({ url: "/admin/mcp/analytics", ...options });

/**
 * Admin List Mcp Audit
 *
 * List MCP audit entries, filtered/paginated server-side (admin only).
 *
 * `outcome` buckets the open outcome vocabulary: 'ok'/'denied' match exactly,
 * 'error' matches any `error:<...>`. `search` is an ILIKE over the argument
 * summary and the acting contact's name/email. Page is clamped to >=1 and
 * page_size to 1..100.
 */
export const adminListMcpAudit = <ThrowOnError extends boolean = false>(
  options?: Options<AdminListMcpAuditData, ThrowOnError>,
) =>
  (options?.client ?? client).get<
    AdminListMcpAuditResponses,
    AdminListMcpAuditErrors,
    ThrowOnError
  >({ url: "/admin/mcp/audit", ...options });

/**
 * Admin Mcp Tool Names
 *
 * Distinct MCP tool names across all time (admin only) — for the audit filter dropdown.
 */
export const adminMcpToolNames = <ThrowOnError extends boolean = false>(
  options?: Options<AdminMcpToolNamesData, ThrowOnError>,
) =>
  (options?.client ?? client).get<AdminMcpToolNamesResponses, unknown, ThrowOnError>({
    url: "/admin/mcp/tools",
    ...options,
  });
